import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import Header from '../components/Header'
import { useI18n } from '../i18n/useI18n'
import { useStore } from '../store/useStore'
import { isWeb3Mode } from '../lib/authMode'

// #1189 Phase 3: user.nationality から local_payment (LPM) が実効的か判定。
// Alipay + WeChat Pay は主に中国訪日客が使う想定なので、中華圏 nationality の
// 場合は先頭にハイライト付きで並べる。それ以外は表示するが順位を下げる。
const _LPM_PRIORITIZED_NATIONALITIES = new Set(['CN', 'HK', 'TW', 'MO'])

const POINT_EXPIRY_DAYS = 180

/**
 * #1188 Phase 2: チャージ手段選択画面。
 * `/charge` (金額選択) → `/charge/method` → 選択に応じて分岐:
 *   - 'card': `/charge/card` (Stripe のカード決済、#1764)
 *   - 'digital_currency': `/charge/confirm` (Web3 mode 限定の旧 mock。TestJPYC を mint)
 *   - 'bank_transfer_jp': `/charge/bank`
 *   - 'revolut': `/charge/revolut` (bank と backend 経路は同じ、marketing コピーだけ差別化)
 *
 * クレジットカード option は本番でも表示するが、**手数料が銀行振込より高い**旨を
 * 各 option の説明文で明示する (客が納得の上で選ぶ形。弊社は Stripe 決済手数料
 * ~3.6-5% を負担するので、料率を客に転嫁して粗利は損なわないようにする)。
 */
export default function ChargeMethodPicker() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useI18n()
  const user = useStore((s) => s.user)
  const nationality = user?.nationality || ''
  const isChineseSpeaking = _LPM_PRIORITIZED_NATIONALITIES.has(nationality)

  // Charge.tsx で state = { amount, fee, total } を渡してくる想定。
  // 金額 unset で直接ここに来た場合は /charge に戻す。
  const state = (location.state ?? {}) as {
    amount?: number
    fee?: number
    total?: number
  }
  if (!state.amount) {
    // 金額 unset で直接ここに来た場合は /charge にリダイレクトさせる
    // (履歴に push せずに置き換え、無限ループ回避)。
    return <Navigate to="/charge" replace />
  }

  type Option = { key: string; label: string; desc: string; to: string; highlight?: boolean }

  // #1188 Phase 2: 日本の方向け / 海外の方向け でグループ化して表示。
  // 実装上はどのグループの option も全ユーザーが選択可能。ラベルはあくまで
  // 想定利用者を示す訴求分類 (「推奨」ではなく利便性ガイド)。
  const groups: Array<{ title: string; options: Option[] }> = [
    {
      title: t('chargeMethod.groupJapanese'),
      options: [
        {
          key: 'bank_transfer_jp',
          label: t('chargeMethod.bankTransfer'),
          desc: t('chargeMethod.bankTransferDesc'),
          to: '/charge/bank',
          highlight: true,
        },
      ],
    },
    {
      title: t('chargeMethod.groupForeigners'),
      options: (() => {
        const lpmOption: Option = {
          key: 'local_payment',
          label: t('chargeMethod.localPayment'),
          desc: t('chargeMethod.localPaymentDesc'),
          to: '/charge/local',
          // 中華圏 nationality の場合のみ LPM を先頭 highlight として推す
          // (Alipay/WeChat Pay は同グループの実利用者)
          highlight: isChineseSpeaking,
        }
        const revolutOption: Option = {
          key: 'revolut',
          label: t('chargeMethod.revolut'),
          desc: t('chargeMethod.revolutDesc'),
          to: '/charge/revolut',
        }
        // #1764 クレジットカードは Stripe で本当に決済する (/charge/card)。本番と demo で同じ経路
        const cardOption: Option = {
          key: 'card',
          label: t('chargeMethod.card'),
          desc: t('chargeMethod.cardDesc'),
          to: '/charge/card',
        }
        return isChineseSpeaking
          ? [lpmOption, revolutOption, cardOption]
          : [revolutOption, lpmOption, cardOption]
      })(),
    },
    // Web3 mode (opt-in の実験) だけ: Sepolia の TestJPYC を発行する旧モック。カードには
    // 請求せず、本物の JPYC でもない。既定の Firebase mode では出さない
    ...(isWeb3Mode()
      ? [{
          title: t('chargeMethod.groupExperimental'),
          options: [{
            key: 'digital_currency',
            label: t('chargeMethod.digitalCurrency'),
            desc: t('chargeMethod.digitalCurrencyDesc'),
            to: '/charge/confirm',
          }],
        }]
      : []),
  ]

  // 有効期限 180 日: 提案書 §4 の資金決済法適用除外要件。
  // 表示は「YYYY/MM/DD まで」形式で「本日から数えて」の直感的表現。
  const expiryDate = new Date()
  expiryDate.setDate(expiryDate.getDate() + POINT_EXPIRY_DAYS)
  const expiryStr = `${expiryDate.getFullYear()}/${(expiryDate.getMonth() + 1)
    .toString()
    .padStart(2, '0')}/${expiryDate.getDate().toString().padStart(2, '0')}`

  return (
    <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      <Header title={t('chargeMethod.title')} onBack={() => navigate(-1)} />

      <div className="flex flex-col items-center py-4">
        <span className="font-body text-xs text-text-secondary">
          {t('chargeMethod.chargeAmount')}
        </span>
        <span className="mt-1 font-display text-[24px] font-bold text-gold">
          &yen; {state.amount.toLocaleString()}
        </span>
      </div>

      <div className="mx-4 border-t border-gray-200" />

      {/* #1188 Phase 2: 有効期限 + 払戻不可の告知 (資金決済法 前払式支払手段
         適用除外の要件、提案書 §4)。金額表示直後にまず知らせる。 */}
      <div className="mx-4 mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
        <p className="font-body text-xs font-semibold text-yellow-900">
          {t('chargeMethod.expiryTitle')}
        </p>
        <p className="mt-1 font-body text-xs leading-5 text-yellow-800">
          {t('chargeMethod.expiryBody', {
            days: POINT_EXPIRY_DAYS,
            date: expiryStr,
          })}
        </p>
      </div>

      <h2 className="px-4 pt-5 pb-3 font-display text-base font-semibold text-text-primary">
        {t('chargeMethod.pickMethod')}
      </h2>

      <div className="flex flex-col gap-4 px-4">
        {groups.map((group) => (
          <div key={group.title} className="flex flex-col gap-2">
            <h3 className="font-body text-xs font-semibold uppercase tracking-wider text-text-secondary">
              {group.title}
            </h3>
            <div className="flex flex-col gap-3">
              {group.options.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => navigate(opt.to, { state })}
                  className={`tap-card flex flex-col items-start rounded-lg border p-4 text-left bg-white transition-all ${
                    opt.highlight
                      ? 'border-2 border-yokoso-red'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex w-full items-center justify-between">
                    <span className="font-display text-base font-semibold text-text-primary">
                      {opt.label}
                    </span>
                    {opt.highlight && (
                      <span className="rounded-full bg-yokoso-red px-2 py-0.5 text-[10px] font-semibold text-white">
                        {t('chargeMethod.recommended')}
                      </span>
                    )}
                  </div>
                  <span className="mt-1 font-body text-xs text-text-secondary">
                    {opt.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mx-4 mt-6 rounded-lg bg-gray-100 p-3">
        <p className="font-body text-xs text-text-secondary">
          {t('chargeMethod.note')}
        </p>
      </div>

      <div className="flex-1" />
    </div>
  )
}
